import React from 'react';
import {Table} from 'react-bootstrap';
import {
  Region,
  Service,
  ServiceCategories,
  ServiceRegionStatus,
  ServiceStatus,
} from '../pages/status-dashboard/StatusDashboardContainer';
import {TFunction, useTranslation} from 'react-i18next';
import {
  operational,
  serviceDown,
  informational,
  serviceDisruption,
  serviceNotAvailable,
} from '../assets/icons';
import {ClientUrls, Status} from '../constants';
import {Link} from 'react-router-dom';
import {getMulticloudServiceCategories} from '../util/config';
import '../assets/sass/multicloudStatusDashboard.scss';

interface MulticloudTablesProps {
  regions: Region[];
  serviceCategories: ServiceCategories;
  serviceRegionStatus: ServiceRegionStatus;
}
interface SortedService {
  serviceId: string;
  service: Service;
}

const getIconElement = (tooltip: string, imgSrc: any, incidentId?: string) =>
  incidentId ? (
    <Link
      data-testid="incident-history-link"
      to={`${ClientUrls.incidentDetailPage}${incidentId}`}>
      <img className="status-icon" src={imgSrc} alt={tooltip} title={tooltip} />
    </Link>
  ) : (
    <img className="status-icon" src={imgSrc} alt={tooltip} title={tooltip} />
  );

const getCellElement = (
  serviceStatus: ServiceStatus,
  t: TFunction<'translation'>,
) => {
  switch (serviceStatus.status) {
    case Status.NormalPerformance:
      return getIconElement(
        t('Common.operational'),
        operational,
        serviceStatus.incidentId,
      );
    case Status.Informational:
      return getIconElement(
        t('Common.informational'),
        informational,
        serviceStatus.incidentId,
      );
    case Status.ServiceDisruption:
      return getIconElement(
        t('Common.serviceDisruption'),
        serviceDisruption,
        serviceStatus.incidentId,
      );
    case Status.ServiceDown:
      return getIconElement(
        t('Common.serviceDown'),
        serviceDown,
        serviceStatus.incidentId,
      );
    default:
      return <div className="status-icon" />;
  }
};

const getStatusElement = (
  serviceStatus: ServiceStatus,
  regionName: string,
  t: TFunction<'translation'>,
) => (
  <div className="service-val" key={regionName}>
    {serviceStatus && serviceStatus.status ? (
      getCellElement(serviceStatus, t)
    ) : (
      <img
        className="status-icon"
        src={serviceNotAvailable}
        alt={t('Common.serviceNotAvailable')}
        title={t('Common.serviceNotAvailable')}
      />
    )}
  </div>
);

const getSortedMulticloudServices = (
  services: Record<string, Service>,
  regionFilter: Region[],
  serviceRegionStatus: ServiceRegionStatus,
): SortedService[] => {
  return Object.keys(services)
    .map((serviceId: string) => ({
      serviceId,
      service: services[serviceId],
    }))
    .filter(({serviceId}) =>
      regionFilter.some(
        (region) => !!serviceRegionStatus?.[serviceId]?.[region.regionId],
      ),
    )
    .sort((a, b) => a.service.serviceName.localeCompare(b.service.serviceName));
};

const getVisibleRegions = (
  services: Record<string, Service>,
  regions: Region[],
  serviceRegionStatus: ServiceRegionStatus,
): Region[] => {
  return regions
    .filter((region) =>
      Object.keys(services).some(
        (serviceId) => !!serviceRegionStatus?.[serviceId]?.[region.regionId],
      ),
    )
    .sort((a, b) => a.regionName.localeCompare(b.regionName));
};

const MULTICLOUD_CATEGORIES = getMulticloudServiceCategories() as string[];

const getMaxColumnCount = (
  regions: Region[],
  serviceCategories: ServiceCategories,
  serviceRegionStatus: ServiceRegionStatus,
): number => {
  return Math.max(
    ...MULTICLOUD_CATEGORIES.map((cat) => {
      const services = serviceCategories[cat] || {};
      return getVisibleRegions(services, regions, serviceRegionStatus).length;
    }),
  );
};

const MulticloudServiceStatusList: React.FC<MulticloudTablesProps> = ({
  regions,
  serviceCategories,
  serviceRegionStatus,
}) => {
  const maxColumnCount = getMaxColumnCount(
    regions,
    serviceCategories,
    serviceRegionStatus,
  );

  const {t} = useTranslation();

  return (
    <>
      {MULTICLOUD_CATEGORIES.map((serviceCategory) => {
        const services = serviceCategories[serviceCategory] || {};
        const visibleRegions = getVisibleRegions(
          services,
          regions,
          serviceRegionStatus,
        );
        const visibleServices = getSortedMulticloudServices(
          services,
          visibleRegions,
          serviceRegionStatus,
        );
        if (!visibleRegions.length || !visibleServices.length) return null;

        const padCount = maxColumnCount - visibleRegions.length;

        return (
          <div
            className="table-container table-responsive status-multicloud-table-container"
            key={serviceCategory}>
            <Table className="status-table-multicloud status-table table-borderless">
              <thead>
                <tr className="status-table-region-header status-table-multicloud-row">
                  <th className="align-middle status-table-multicloud-header">
                    {serviceCategory === MULTICLOUD_CATEGORIES[0] &&
                      (t('StatusDashboard.productsServices') as string)}
                  </th>
                  {visibleRegions.map((region: Region) => (
                    <th
                      data-testid="live-status-region-name"
                      key={region.regionName}
                      className="align-middle region-col status-table-multicloud-header">
                      {region.regionName}
                    </th>
                  ))}
                  {/* {padCount > 0 &&
                    Array.from({length: padCount}).map((_, idx) => (
                      <th
                        key={`pad-head-${idx}`}
                        className="align-middle region-col blank-region status-table-multicloud-header"></th>
                    ))} */}
                </tr>
              </thead>
              <tbody>
                <tr className="service-category status-table-multicloud-row">
                  <th className="category status-multicloud-tooltip status-table-multicloud-header">
                    {serviceCategory}
                  </th>
                  <td
                    className="status-table-multicloud-cell"
                    colSpan={maxColumnCount}></td>
                </tr>
                {visibleServices.map((sortedService: SortedService) => (
                  <tr
                    className="status-table-multicloud-row"
                    key={sortedService.serviceId}
                    style={{backgroundColor: 'white'}}>
                    <th className="left-heading status-multicloud-tooltip status-table-multicloud-header">
                      {sortedService.service.serviceName}
                    </th>
                    {visibleRegions.map((region: Region) => (
                      <td
                        key={`${sortedService.serviceId}${region.regionName}`}
                        className="align-middle region-col status-table-multicloud-cell">
                        {getStatusElement(
                          serviceRegionStatus[sortedService.serviceId]?.[
                            region.regionId
                          ],
                          region.regionName,
                          t,
                        )}
                      </td>
                    ))}
                    {/* {padCount > 0 &&
                      Array.from({length: padCount}).map((_, idx) => (
                        <td
                          key={`pad-td-${idx}`}
                          className="align-middle region-col blank-region status-table-multicloud-cell"></td>
                      ))} */}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        );
      })}
    </>
  );
};

export default MulticloudServiceStatusList;
